/**
 * THE ADVICE BAND — what changes what the player should DO, never a figure (§91).
 *
 * Only the band is here. The instrument panel behind `F1` — `BLOCK`, `GROUND`, the draw calls, the
 * verdict log — is NOT translated and that is the decision of §142.4: it speaks to whoever is building
 * this. Translating it would blur the line that decides what may never be one keypress from invisible.
 */
export default {
  drone_pick: 'drone on the ground — 4 to pick it up',
  drone_land: 'SET IT DOWN to stow it · right button descends',
  drone_walk: 'it is down and far: walk to it to stow it',
  /** The key comes from the KEYMAP and is never typed into the sentence: a screen that teaches a letter
   *  somebody remapped teaches the wrong key (`keymap.ts keyLabel`, §85.2). */
  drone_cut: '{key} · CUT THE LINK — the drone stays here and you can come back for it',
  /** Why `4` did nothing: the authority refuses a launch with no clear metre, and a key that refuses in
   *  silence is half a mechanic — the same argument `drone_land` already makes. */
  drone_pad_blocked: '🛬 No room to launch — the square in front of you is not clear. Take a step.',
  /**
   * THE THREE WAYS A FLIGHT ENDS, said to the pilot (`drone.md §5`).
   *
   * The authority names the CAUSE and the page writes the sentence, which is what lets it be translated at
   * all. Each one says what happened AND what is left to do about it: the aircraft is not destroyed — it
   * falls, and it is still out there — so a line that only announced the loss would hide the half of the
   * mechanic the player can still act on.
   */
  drone_lost_crash: '🚁 You flew it into something. It is down where it hit — go and get it.',
  drone_lost_battery: '🔋 Batteries flat. It came down on its own — walk over and pick it up.',
  drone_lost_rival: '🚁 Somebody plucked your drone out of the air. It is theirs to laugh about now.',
  // States the authority put the player in. `…` while a transition is in flight: he asked, and he is
  // still being SCORED in the old stance.

  // THE TITLE IS SPLIT IN TWO because the layout draws it in two pieces — the headline on the left and
  // how it closes on the right, separated by their rule. One string with the `<b>` inside forced markup
  // into the catalogue to split it again, which is the copy deciding the layout.
  map_title_plain: 'Field map',
  map_close: 'M TO CLOSE · THE MATCH GOES ON',
  map_close_btn: 'Close the map',
  map_legend: 'Key',
  map_you: 'Your position',
  map_structures: 'Structures',
  kneeling: 'KNEELING…',
  standing: 'STANDING…',
  kneel: 'KNEELING',
  prone: 'PRONE',
  proning: 'GOING PRONE…',
  stealth: 'STEALTH',
  down: 'DOWN — back up shortly',
  out: 'OUT OF THE ROUND',
  sanctioned: 'SANCTIONED — {s} s',
  // The fall (§14). It says the HEIGHT because that is the one fact the player could not see for himself —
  // a drop with no number on it reads as the terrain having eaten somebody.
  fell: 'FELL — {m} m',
  // The swap: for those milliseconds the trigger does not answer, and a mute trigger with nothing on
  // screen explaining it reads as a hung page.
  swapping: 'SWAPPING…',
  // PROD-CLEANUP: the band draws the swap and not the reload, so this has no reader yet. Kept because the
  // two mute the trigger for the same reason, and drawing one without the other is the asymmetry, not the
  // key. Delete it with the rest if the band is decided against it.
  // PROD-CLEANUP: no call site — the reload state is drawn from the snapshot, not from this string.
  reloading: 'RELOADING…',
  thirsty: 'YOU ARE DEHYDRATING',
  // The can in hand: in this state the trigger throws instead of firing, which is the one thing about it
  // a player cannot work out by looking.
  grenade_ready: 'CAN IN HAND — CLICK TO THROW',
  // The link. It may never be a note that scrolls away (§30).
  offline: 'OFFLINE — reconnecting ({n})',
  reconnected: 'reconnected as #{id} — new spawn, full magazine',
  // Local match: it does not score for records, and saying so is the whole point (§140).
  local: 'PRACTICE — local match, does not count for records',
  // The labels of the two field instruments, which are WORDS and not figures.
  wind: 'WIND',
  position: 'POSITION',
  // The eight points of the compass strip, CLOCKWISE FROM NORTH. Abbreviations and not names: the tape is
  // ten pixels tall, and five of the eight are the same letters in both languages — the west half is not
  // (`W` against `O`), which is exactly why they are catalogue entries and not a typed ring.
  compass_n: 'N',
  compass_ne: 'NE',
  compass_e: 'E',
  compass_se: 'SE',
  compass_s: 'S',
  compass_sw: 'SW',
  compass_w: 'W',
  compass_nw: 'NW',

  // ── the link, said twice on purpose ───────────────────────────────────────────────────────────────
  // The band says WHICH STATE the link is in and the log says what just CHANGED. One without the other
  // is either a badge nobody re-reads or a line that scrolls away with the only warning on it (§30).
  connecting: 'connecting…',
  link_lost: 'link lost — reconnecting',
  link_stale: 'RELOAD — this page cannot talk to this server',
  link_stale_note: 'this page cannot talk to this server — RELOAD (ctrl+shift+R)',
  local_crashed: 'the local match went down ({why}) — reload',
  field_not_fetched: 'could not fetch the field ({why}) — reconnecting to receive it over the socket',
  // Blocking the close is only half of it: a combination that stops working without saying so reads as a
  // broken keyboard, so the block names the way out it left open.
  tab_close_blocked: 'tab close blocked — leave with ESC and close from the pause screen',

  // ── what the band is allowed to carry ─────────────────────────────────────────────────────────────
  // The gait that decides whether anybody hears you, and the two figures a player acts on (§91). Every
  // other number lives in the bottom-right stack or behind `F1`.
  running: 'running',
  warnings: 'warnings {n}/{of}',
  impact: 'impact',

  // ── the can ───────────────────────────────────────────────────────────────────────────────────────
  // The COLOUR is named because the wheel does not say what it is selecting (§38.1), and the dyes are
  // named here rather than beside the seal colours: one is smoke, the other is a tape on the field.
  smoke_note: 'smoke {dye} · WHEEL',
  impact_note: 'grenade · WHEEL',
  smoke_lit: 'smoke {dye} — {s} s',
  smoke_refused: 'SMOKE — {reason}',
  smoke_too_many: 'too much smoke on the field, wait',
  dye_red: 'red',
  dye_yellow: 'yellow',
  dye_blue: 'blue',
  dye_white: 'white',

  // ── refusals the player could not have predicted ──────────────────────────────────────────────────
  // A body hit that did not pay for lack of energy is NOT cover, and may not be said as one: it arrived,
  // at the right person. Naming it after cover sends somebody looking for a wall that is not there.
  no_power: 'NO POWER · {d} m — your maximum is {max} m',
  climb_refused: 'could not climb · {reason}',
  item_used: 'used: {item}',
  item_refused: 'could not use {item} · {reason}',
  item_name_canteen: 'the canteen',
  item_name_binoculars: 'the binoculars',
  item_name_camo: 'the camo',
  item_name_radio: 'the radio',

  // The set's own foot. It names the KEYS and not the drawn pad: the pad answers a pointer, and the
  // pointer is held by the reticle. The letters travel in from `keyLabel`, never typed in a catalogue.
  radio_tx_on: 'TRANSMITTING',
  radio_tx_push: 'PUSH TO TALK',
  radio_refused_no_radio: 'you did not bring a radio',
  radio_refused_no_such_channel: 'no such channel',
  radio_refused_no_such_frequency: 'that frequency is out of the set\'s range',
  radio_refused_down: 'you are out of the round',
  item_not_carried: 'you did not bring one',
  item_not_built: 'it does nothing yet',
  item_already_full: 'no need',
  item_down: 'you are out of play',
  // Cosmetic, and the stand-in covers it — but a silent fallback is how "why does it look like the old
  // build" starts. The notice says how to recover, because the automatic retry is already spent.
  marker_model_note: 'marcadora model unavailable — stand-in in use',
  marker_model_notice: 'MARCADORA MODEL UNAVAILABLE — pick it again with L',

  // ── the field changing under a live page ──────────────────────────────────────────────────────────
  // Both end in a reload, and which one it was is the whole message: naming the field the player is
  // already standing on turns a correct reload into something that reads as a bug.
  field_changing: 'field: {name} — reloading…',
  session_rearmed: 'session rearmed on {name} — reloading…',

  // ── the handshake refusing, said as something to DO ───────────────────────────────────────────────

  // The authority sends three causes and each is fixed somewhere else, so they may not collapse into one
  // sentence with no action in it. `{why}` carries the version, the hash or the cap — the half that is
  // for whoever is building this, and empty when the service sent no detail.
  refused_stale: 'REFUSED — this page is older than the service: RELOAD (ctrl+shift+R){why}',
  refused_full: 'REFUSED — the room is full: wait for a place, or start a solo match from the menu{why}',
  refused_other: 'REFUSED — the service would not take this page{why}',
  /** The host closed the room. There is no authority left and none is migrated (`hosted.md §6.6`), so
   *  this is an ending and not an outage: it says what happened and what can be done next. */
  /** The pass or the bearer aged out under a live page (T7). Terminal: retrying is refused for ever. */
  session_elsewhere: 'this account signed in somewhere else — the field belongs to that browser now',
  session_expired: 'your session expired — sign in again from the phone to come back in',
  host_gone: 'SERVER DISCONNECTED — whoever was hosting closed the room, so this match is over. Ask them to open it again, or start a solo match from the menu.',
  /** The invitation, which is the whole room: a code and the address that carries it (`hosted.md §9`).
   *  Held on screen for two minutes because the host has to read it out or paste it somewhere. */
  room_open: 'ROOM {code} — {seats} seats. Send this: {url}',
  /** How one peer got through. `relay` is the case that costs us the session's bandwidth (`ops/turn.md §5`). */
  room_peer: '{peer} is in — {how}, {ms} ms to connect',
  /** No relay on offer, so a peer behind CGNAT will simply not connect (`hosted.md §7.2`). Said before it
   *  happens, because the symptom without it is a guest that waits and is never told why. */
  room_no_relay: 'no relay on offer — a player behind CGNAT may not be able to connect',
  /** The FIELD refuses the out-of-body camera (`MapDef.thirdPerson`). The key still ANSWERS: one that
   *  refuses in silence is half a mechanic, and the tester presses it, nothing happens, and he suspects the
   *  game. The key comes from the KEYMAP and is never typed into the sentence. */
  third_person_refused: '{key} · this field is played from the eye',
  flag_take: '🏁 {key} — take {team}’s flag',
};
